/**
 * Vite version of ProductDetail.
 * Replaces next/image → <img>, next/navigation useRouter → react-router useNavigate.
 * Removes 'use client' directive (no-op in Vite).
 *
 * Purpose: Full product detail with color-swatch + size-button variant picker.
 * Inputs:  product (Product) — variants carry optional .color and .size fields.
 * Outputs: Selected variant fed to useCart().add(); navigates to /cart on success.
 * Constraints: Cart contract unchanged (variantId/variantLabel preserved exactly).
 *              ProductDetail receives Product; does NOT call the API itself.
 */

import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router'
import type { Product, Variant } from '@/lib/types'
import { useCart } from '../CartProvider'
import { MAX_QTY_PER_LINE } from '@/lib/cart/constants'
import { clampQuantity } from '@/lib/cart/validate'
import { VetDiscountBanner } from './VetDiscountBanner'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`
}

/**
 * Map a color name (as returned by Printify variant labels) to a CSS hex color.
 * Falls back to a neutral navy-ish swatch for unknowns.
 */
const COLOR_MAP: Record<string, string> = {
  // Blacks / grays
  'black':              '#111111',
  'charcoal':           '#374151',
  'heather charcoal':   '#4b5563',
  'dark grey':          '#4b5563',
  'dark gray':          '#4b5563',
  'heather grey':       '#9ca3af',
  'heather gray':       '#9ca3af',
  'gray':               '#9ca3af',
  'grey':               '#9ca3af',
  'white':              '#f9fafb',
  'natural':            '#fef9f0',
  'cream':              '#fffbeb',
  // Navy / blues
  'navy':               '#1e3a5f',
  'dark navy':          '#0e2340',
  'navy blue':          '#1e3a5f',
  'royal blue':         '#1d4ed8',
  'blue':               '#3b82f6',
  'steel blue':         '#4682b4',
  'sky blue':           '#7dd3fc',
  'light blue':         '#93c5fd',
  'columbia blue':      '#9dc3e6',
  // Reds
  'red':                '#dc2626',
  'cardinal':           '#9b111e',
  'dark red':           '#991b1b',
  'maroon':             '#7f1d1d',
  'crimson':            '#b91c1c',
  'rust':               '#b45309',
  // Greens
  'olive':              '#4b5320',
  'od green':           '#4b5320',
  'military green':     '#4a5c3d',
  'dark green':         '#14532d',
  'green':              '#16a34a',
  'forest':             '#166534',
  'forest green':       '#166534',
  'hunter green':       '#1a3a1a',
  'sage':               '#84a98c',
  'camo':               '#78866b',
  // Tans / browns
  'tan':                '#c8a97e',
  'tan / khaki':        '#c8a97e',
  'khaki':              '#c3b091',
  'coyote':             '#81613c',
  'coyote tan':         '#81613c',
  'brown':              '#78350f',
  'sand':               '#c2b280',
  // Others
  'burgundy':           '#800020',
  'purple':             '#7e22ce',
  'orange':             '#ea580c',
  'gold':               '#b45309',
  'yellow':             '#ca8a04',
  'pink':               '#ec4899',
}

function colorToHex(name: string): string {
  const key = name.toLowerCase().trim()
  return COLOR_MAP[key] ?? '#1e3a5f' // fallback: dark navy
}

/**
 * Reduce any color label (variant name like "Dark Navy" / "Tan / Khaki", or a
 * mockup alt suffix like "… — Navy") to a canonical token so swatch selections
 * match the correct per-color product image regardless of naming differences.
 */
function canonColor(s: string): string {
  const t = s.toLowerCase()
  if (t.includes('navy')) return 'navy'
  if (t.includes('tan') || t.includes('khaki') || t.includes('sand')) return 'tan'
  if (t.includes('olive') || t.includes('od green')) return 'olive'
  if (t.includes('spruce') || t.includes('forest') || t.includes('green')) return 'spruce'
  if (t.includes('black')) return 'black'
  return t.trim()
}

/**
 * Derive a color name from a variant label when variant.color is not set.
 * Attempts to match "Black", "Navy", "Tan / Khaki", etc. at the start of
 * typical Printify labels like "Black / S", "Navy / L".
 */
function extractColorFromLabel(label: string): string | null {
  // Printify label pattern: "<Color> / <Size>" or just "<Color>"
  const match = label.match(/^([^/]+?)(?:\s*\/\s*.+)?$/)
  if (!match) return null
  const candidate = match[1].trim()
  // Only return if it maps to something we know, or it looks like a color word
  const key = candidate.toLowerCase()
  if (COLOR_MAP[key]) return candidate
  // If it looks like a size (S, M, L, XL, 2XL, etc.) skip it
  if (/^\d*X?[SLMX]{1,3}$/i.test(candidate)) return null
  return candidate
}

/**
 * Derive a size from variant label: "Black / XL" → "XL"
 */
function extractSizeFromLabel(label: string): string | null {
  const match = label.match(/\/\s*(\d*X?[SLMX]{1,3}(?:\+)?|\d+XL?)/i)
  return match ? match[1].toUpperCase() : null
}

/**
 * Branch slugs recognized by the label-fallback parser. Restricted to a known
 * set so common color words in Printify labels (e.g. "Navy / S") never get
 * misread as a branch. The DB variant row's explicit `branch` field is always
 * preferred; this fallback only fires for rows that omit `branch`.
 */
const KNOWN_BRANCH_SLUGS = new Set([
  'army', 'navy', 'air-force', 'marines', 'marine-corps',
  'coast-guard', 'space-force',
])

/**
 * Derive a branch name from a variant label like "Army / S" → "Army".
 * Returns null when the first segment is a known color (avoids reading
 * "Navy / S" as branch=Navy on a normal color+size apparel variant), when it
 * looks like a bare size, or when it isn't one of the recognized branches.
 */
export function extractBranchFromLabel(label: string): string | null {
  const match = label.match(/^([^/]+?)(?:\s*\/\s*.+)?$/)
  if (!match) return null
  const candidate = match[1].trim()
  const key = candidate.toLowerCase()
  if (COLOR_MAP[key]) return null
  if (/^\d*X?[SLMX]{1,3}$/i.test(candidate)) return null
  return KNOWN_BRANCH_SLUGS.has(key.replace(/\s+/g, '-')) ? candidate : null
}

// ─── Derived state helpers ────────────────────────────────────────────────────

export interface NormalizedVariant extends Variant {
  _color: string   // resolved color name
  _hex:   string   // resolved hex
  _size:  string | null
  _branch: string | null  // resolved branch name (null = product has no branch dimension)
}

export function normalizeVariants(variants: Variant[]): NormalizedVariant[] {
  return variants.map((v) => {
    const colorName  = v.color  ?? extractColorFromLabel(v.label) ?? 'Unknown'
    const sizeName   = v.size   ?? extractSizeFromLabel(v.label)
    const branchName = v.branch ?? extractBranchFromLabel(v.label)
    return {
      ...v,
      _color: colorName,
      _hex:   colorToHex(colorName),
      _size:  sizeName,
      _branch: branchName,
    }
  })
}

/**
 * True iff the variants span 2+ distinct branches — the gate for rendering
 * the Branch picker. Single-branch / no-branch products render exactly as
 * before (zero behavior change).
 */
export function hasMultipleBranches(variants: NormalizedVariant[]): boolean {
  const seen = new Set<string>()
  for (const v of variants) {
    if (v._branch) {
      seen.add(v._branch)
      if (seen.size >= 2) return true
    }
  }
  return false
}

/**
 * Distinct branch names (original casing) in first-seen order. Empty when no
 * variant carries a branch.
 */
export function distinctBranches(variants: NormalizedVariant[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const v of variants) {
    if (v._branch && !seen.has(v._branch)) {
      seen.add(v._branch)
      out.push(v._branch)
    }
  }
  return out
}

/**
 * Resolve the active variant from the current color/size/branch selection.
 * Branch+size+color must ALL match the chosen variant row (or be ignored when
 * the corresponding dimension is absent for this product). This is the
 * correctness-critical path — a wrong match ships the wrong product.
 *
 * Fallback chain: exact match → branch-only match → first variant. The branch
 * fallback matters when the selected size doesn't exist for the chosen branch
 * (shouldn't happen because the size list is filtered per-branch, but guards
 * against stale selectedSize state).
 */
export function resolveActiveVariant(
  variants: NormalizedVariant[],
  ctx: {
    hasColors: boolean
    hasSizes: boolean
    hasBranches: boolean
    selectedColor: string
    selectedSize: string | null
    selectedBranch: string
  },
): NormalizedVariant | null {
  if (!ctx.hasColors && !ctx.hasSizes && !ctx.hasBranches) {
    return variants[0] ?? null
  }
  return (
    variants.find((v) => {
      const colorMatch  = !ctx.hasColors || v._color === ctx.selectedColor
      const sizeMatch   = !ctx.hasSizes || v._size === ctx.selectedSize
      const branchMatch = !ctx.hasBranches || v._branch === ctx.selectedBranch
      return colorMatch && sizeMatch && branchMatch
    }) ??
    variants.find((v) => !ctx.hasBranches || v._branch === ctx.selectedBranch) ??
    variants[0] ??
    null
  )
}

/**
 * Find the gallery image index for a branch. Matches the branch slug in the
 * image URL first (most reliable — branch mockup keys are SKU__type__color__branch),
 * then falls back to alt-text containment. Returns -1 when no match.
 */
export function findBranchImageIndex(
  images: { url: string; alt: string }[],
  branchName: string,
): number {
  if (!branchName) return -1
  const slug = branchName.trim().toLowerCase().replace(/\s+/g, '-')
  const urlIdx = images.findIndex((img) => img.url.includes(`__${slug}`))
  if (urlIdx >= 0) return urlIdx
  const branchText = branchName.trim().toLowerCase()
  return images.findIndex((img) => img.alt.toLowerCase().includes(branchText))
}

// Trust signals + product facts shown on every PDP (static; icon paths are Feather-style).
const TRUST_SIGNALS: { label: string; path: string }[] = [
  { label: 'Secure checkout', path: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' },
  { label: 'Printed in the USA', path: 'M6 19v2m12-2v2M4 10h16M6 10V7a6 6 0 0112 0v3M5 10h14l-1 9H6l-1-9z' },
  { label: 'Veteran-owned', path: 'M12 15l-5 3 2-6-5-4h6l2-6 2 6h6l-5 4 2 6z' },
]

const PRODUCT_FACTS: { term: string; desc: string }[] = [
  { term: 'Made to order', desc: 'Each item is embroidered or printed for you — production takes 2–5 business days.' },
  { term: 'Shipping', desc: 'FREE U.S. shipping on orders over $50 ($4.99 flat under). Arrives in 6–11 business days.' },
  { term: 'Guarantee', desc: 'Misprints, damage, or defects replaced or refunded free — contact us within 30 days of delivery.' },
  { term: 'Gives back', desc: 'A portion of every order supports veteran causes.' },
]

// ─── Sub-components ───────────────────────────────────────────────────────────

interface SwatchButtonProps {
  color: string
  hex: string
  selected: boolean
  onSelect: () => void
}

function SwatchButton({ color, hex, selected, onSelect }: SwatchButtonProps) {
  const isLight = ['white', 'natural', 'cream', 'sky blue', 'light blue', 'columbia blue', 'khaki', 'sand', 'sage', 'heather grey', 'heather gray', 'gray', 'grey']
    .includes(color.toLowerCase())

  return (
    <button
      type="button"
      aria-label={color}
      aria-pressed={selected}
      title={color}
      onClick={onSelect}
      className={[
        'w-9 h-9 rounded-full border-2 transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold',
        selected
          ? 'border-gold scale-110 shadow-lg shadow-gold/30'
          : 'border-transparent hover:border-white/40',
        isLight ? 'ring-1 ring-white/20' : '',
      ].join(' ')}
      style={{ background: hex }}
    />
  )
}

interface SizeButtonProps {
  size: string
  selected: boolean
  available: boolean
  onSelect: () => void
}

function SizeButton({ size, selected, available, onSelect }: SizeButtonProps) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-disabled={!available}
      onClick={available ? onSelect : undefined}
      className={[
        'min-w-[2.75rem] px-3 py-1.5 rounded-md border text-sm font-semibold transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold',
        selected
          ? 'bg-gold border-gold text-white'
          : available
            ? 'bg-transparent border-white/25 text-white hover:border-gold/60 hover:text-gold'
            : 'bg-transparent border-white/10 text-white/30 cursor-not-allowed',
      ].join(' ')}
    >
      {size}
    </button>
  )
}

interface BranchButtonProps {
  branch: string
  selected: boolean
  onSelect: () => void
}

/**
 * Branch selector button — mirrors SizeButton styling so the branch picker
 * reads as the same affordance family. aria-pressed conveys selection state
 * to assistive tech.
 */
function BranchButton({ branch, selected, onSelect }: BranchButtonProps) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-label={branch}
      title={branch}
      onClick={onSelect}
      className={[
        'px-4 py-2 rounded-md border text-sm font-semibold transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold',
        selected
          ? 'bg-gold border-gold text-white'
          : 'bg-transparent border-white/25 text-white hover:border-gold/60 hover:text-gold',
      ].join(' ')}
    >
      {branch}
    </button>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export function ProductDetail({ product }: { product: Product }) {
  const navigate = useNavigate()
  const { add } = useCart()

  const rawVariants: Variant[] = Array.isArray(product.variants) ? product.variants : []
  const variants = useMemo(() => normalizeVariants(rawVariants), [rawVariants])

  // Distinct color options (deduplicated by name)
  const colors: { name: string; hex: string }[] = useMemo(() => {
    const seen = new Set<string>()
    return variants.reduce<{ name: string; hex: string }[]>((acc, v) => {
      if (!seen.has(v._color)) {
        seen.add(v._color)
        acc.push({ name: v._color, hex: v._hex })
      }
      return acc
    }, [])
  }, [variants])

  const hasColors = colors.length > 1

  // Whether this product has sizes at all
  const hasSizes = useMemo(() => variants.some((v) => v._size !== null), [variants])

  // Branch dimension — only present on multi-branch products (e.g. VET-HOOD-001
  // Branch Hoodie, which has 6 branch variants all with color=Navy). Single-
  // branch / branchless products (caps, tees) skip the picker entirely.
  const branches = useMemo(() => distinctBranches(variants), [variants])
  const hasBranches = branches.length >= 2

  // Selected color — default to first color
  const [selectedColor, setSelectedColor] = useState<string>(colors[0]?.name ?? '')

  // Selected branch — default to first branch. Only meaningful when hasBranches.
  const [selectedBranch, setSelectedBranch] = useState<string>(branches[0] ?? '')

  // Sizes available for the current selection (filtered by branch when the
  // product has a branch dimension, and by color otherwise).
  const sizesForSelection = useMemo<string[]>(() => {
    if (!hasSizes) return []
    const order = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL']
    const raw = variants
      .filter((v) => {
        const colorMatch  = v._color === selectedColor
        const branchMatch = !hasBranches || v._branch === selectedBranch
        return colorMatch && branchMatch && v._size !== null
      })
      .map((v) => v._size as string)
    const unique = [...new Set(raw)]
    unique.sort((a, b) => {
      const ia = order.indexOf(a)
      const ib = order.indexOf(b)
      if (ia >= 0 && ib >= 0) return ia - ib
      if (ia >= 0) return -1
      if (ib >= 0) return 1
      return a.localeCompare(b)
    })
    return unique
  }, [variants, selectedColor, selectedBranch, hasSizes, hasBranches])

  const showSizeSelector = hasSizes && sizesForSelection.length > 1

  // Selected size — default to first available for chosen branch+color
  const [selectedSize, setSelectedSize] = useState<string | null>(
    () => sizesForSelection[0] ?? null
  )

  // Resolve the active variant from branch + color + size selection. The
  // exact-variant match is what makes the cart line and the downstream Printful
  // fulfillment correct (variant_id like VET-HOOD-001-army-S resolves to the
  // Army front print file via resolvePrintFileUrl).
  const activeVariant: NormalizedVariant | null = useMemo(
    () =>
      resolveActiveVariant(variants, {
        hasColors: colors.length > 1,
        hasSizes,
        hasBranches,
        selectedColor,
        selectedSize,
        selectedBranch,
      }),
    [variants, colors.length, hasSizes, hasBranches, selectedColor, selectedSize, selectedBranch],
  )

  // ── Image gallery ──
  const galleryImages = product.images ?? []
  // Lazy-init the hero index to the default branch's mockup when the product
  // has a branch dimension — otherwise the alpha-sorted gallery would show a
  // different branch than the (DB-order) default selected branch on first paint.
  const [selectedImageIdx, setSelectedImageIdx] = useState(() => {
    if (hasBranches && branches[0]) {
      const idx = findBranchImageIndex(
        galleryImages.map((i) => ({ url: i.url, alt: i.alt })),
        branches[0],
      )
      if (idx >= 0) return idx
    }
    return 0
  })

  // Index of the first image whose alt matches a given color token.
  const imageIdxForColor = useMemo(() => {
    return (colorName: string): number => {
      const want = canonColor(colorName)
      const i = galleryImages.findIndex((img) => {
        const suffix = img.alt?.split('—').pop() ?? img.alt ?? ''
        return canonColor(suffix) === want
      })
      return i
    }
  }, [galleryImages])

  const heroImg = galleryImages[selectedImageIdx]?.url ?? galleryImages[0]?.url ?? null

  const [quantity, setQuantity] = useState(1)

  function handleColorSelect(colorName: string) {
    setSelectedColor(colorName)
    // Switch the hero image to this color's photoreal render when one exists.
    // (Branch-dimension products have a single color, so branch selection owns
    // the hero — color picker is hidden when hasBranches is true.)
    const idx = imageIdxForColor(colorName)
    if (idx >= 0) setSelectedImageIdx(idx)
    // Reset size to first available for new color
    const newSizes = variants
      .filter((v) => v._color === colorName && v._size !== null)
      .map((v) => v._size as string)
    if (newSizes.length > 0) {
      setSelectedSize(newSizes[0])
    }
  }

  function handleBranchSelect(branchName: string) {
    setSelectedBranch(branchName)
    // Switch the hero image to this branch's mockup when one exists.
    const idx = findBranchImageIndex(
      galleryImages.map((i) => ({ url: i.url, alt: i.alt })),
      branchName,
    )
    if (idx >= 0) setSelectedImageIdx(idx)
    // Reset size to first available for the new branch (size run may differ
    // per branch — defensive; current hoodie run is uniform across branches).
    const newSizes = variants
      .filter((v) => v._branch === branchName && v._color === selectedColor && v._size !== null)
      .map((v) => v._size as string)
    if (newSizes.length > 0) {
      setSelectedSize(newSizes[0])
    }
  }

  function handleAdd() {
    add({
      productId:      product.id,
      sku:            product.sku,
      name:           product.name,
      imageUrl:       heroImg,
      variantId:      activeVariant?.variant_id ?? null,
      variantLabel:   activeVariant?.label ?? null,
      unitPriceCents: product.price_cents,
      quantity,
    })
    navigate('/cart')
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16 pb-24 md:pb-0">

      {/* ── Image + thumbnail gallery ── */}
      <div className="flex flex-col gap-3">
        <div className="relative aspect-square bg-navy/50 rounded-2xl flex items-center justify-center overflow-hidden border border-white/8 shadow-2xl shadow-black/40">
          {heroImg ? (
            <img
              key={heroImg}
              src={heroImg}
              alt={galleryImages[selectedImageIdx]?.alt || product.name}
              width={1000}
              height={1000}
              className="w-full h-full object-cover transition-opacity duration-300"
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
          ) : (
            <div className="flex flex-col items-center gap-3 text-white/20">
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none" aria-hidden="true">
                <rect width="64" height="64" rx="8" fill="currentColor" fillOpacity="0.1"/>
                <path d="M16 48l12-16 8 10.667 6-8L48 48H16z" fill="currentColor" fillOpacity="0.3"/>
                <circle cx="24" cy="24" r="4" fill="currentColor" fillOpacity="0.3"/>
              </svg>
              <span className="text-sm">Image coming soon</span>
            </div>
          )}
        </div>

        {galleryImages.length > 1 && (
          <div className="flex flex-wrap gap-2.5" role="group" aria-label="Product images">
            {galleryImages.map((img, i) => (
              <button
                key={img.url}
                type="button"
                aria-label={img.alt || `View ${i + 1}`}
                aria-pressed={i === selectedImageIdx}
                onClick={() => setSelectedImageIdx(i)}
                className={[
                  'w-16 h-16 rounded-lg overflow-hidden border-2 bg-navy/50 transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold',
                  i === selectedImageIdx ? 'border-gold' : 'border-white/10 hover:border-white/40',
                ].join(' ')}
              >
                <img
                  src={img.thumbUrl ?? img.url}
                  alt=""
                  width={512}
                  height={512}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Info & picker ── */}
      <div className="flex flex-col justify-start py-1">
        <h1 className="text-3xl lg:text-4xl font-extrabold tracking-tight mb-2 leading-tight">
          {product.name}
        </h1>

        <p className="text-2xl lg:text-3xl text-gold-text font-bold mb-2">
          {formatPrice(product.price_cents)}
        </p>

        {/* Veteran discount affordance — compact single-line note near price */}
        <div className="mb-4">
          <VetDiscountBanner compact />
        </div>

        {product.description && (
          <p className="text-white/65 mb-6 leading-relaxed text-sm lg:text-base max-w-prose">
            {product.description}
          </p>
        )}

        {/* ── Color swatches ── */}
        {hasColors && (
          <div className="mb-6">
            <p className="text-sm font-semibold mb-3 flex items-center gap-2">
              Color:
              <span className="text-gold font-normal">{selectedColor}</span>
            </p>
            <div className="flex flex-wrap gap-2.5" role="group" aria-label="Choose color">
              {colors.map(({ name, hex }) => (
                <SwatchButton
                  key={name}
                  color={name}
                  hex={hex}
                  selected={selectedColor === name}
                  onSelect={() => handleColorSelect(name)}
                />
              ))}
            </div>
          </div>
        )}

        {/* ── Branch selector (multi-branch products only — e.g. VET-HOOD-001
             Branch Hoodie). Rendered ABOVE the size picker so the customer
             picks their service branch first, then size. Selecting a branch
             filters the size list to that branch's run and switches the hero
             image to that branch's mockup. Single-branch products skip this
             block entirely (zero behavior change). */}
        {hasBranches && (
          <div className="mb-6">
            <p className="text-sm font-semibold mb-3 flex items-center gap-2">
              Branch:
              <span className="text-gold font-normal">{selectedBranch}</span>
            </p>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Choose branch">
              {branches.map((branch) => (
                <BranchButton
                  key={branch}
                  branch={branch}
                  selected={selectedBranch === branch}
                  onSelect={() => handleBranchSelect(branch)}
                />
              ))}
            </div>
          </div>
        )}

        {/* ── Size buttons ── */}
        {showSizeSelector && (
          <div className="mb-6">
            <p className="text-sm font-semibold mb-3">Size:</p>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Choose size">
              {sizesForSelection.map((size) => (
                <SizeButton
                  key={size}
                  size={size}
                  selected={selectedSize === size}
                  available
                  onSelect={() => setSelectedSize(size)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Single-variant fallback label (no picker needed) */}
        {!hasColors && !showSizeSelector && variants.length === 1 && (
          <p className="text-sm text-white/50 mb-6">{variants[0].label}</p>
        )}

        {/* ── Quantity ── */}
        <div className="mb-8">
          <label htmlFor="qty" className="block text-sm font-semibold mb-2">
            Quantity
          </label>
          <div className="flex items-center gap-3">
            <button
              type="button"
              aria-label="Decrease quantity"
              onClick={() => setQuantity((q) => clampQuantity(q - 1))}
              className="w-9 h-9 rounded-md border border-white/20 hover:border-gold/50 text-white font-bold text-lg flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
            >
              −
            </button>
            <input
              id="qty"
              type="number"
              min={1}
              max={MAX_QTY_PER_LINE}
              value={quantity}
              onChange={(e) =>
                setQuantity(clampQuantity(Number.parseInt(e.target.value, 10)))
              }
              className="w-16 bg-navy-deep border border-gold/30 rounded-md px-3 py-2 text-white text-center font-semibold focus:outline-none focus:ring-2 focus:ring-gold"
            />
            <button
              type="button"
              aria-label="Increase quantity"
              onClick={() => setQuantity((q) => clampQuantity(q + 1))}
              className="w-9 h-9 rounded-md border border-white/20 hover:border-gold/50 text-white font-bold text-lg flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
            >
              +
            </button>
          </div>
        </div>

        {/* ── Add to cart ── */}
        <button
          type="button"
          onClick={handleAdd}
          disabled={!activeVariant && rawVariants.length > 0}
          className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-gold text-white font-bold px-10 py-4 rounded-lg hover:bg-gold-bright transition-colors text-base shadow-lg shadow-gold/25 disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-black"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z"/>
          </svg>
          Add to cart
        </button>

        {/* ── Trust signals ── */}
        <ul className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-white/70">
          {TRUST_SIGNALS.map(({ label, path }) => (
            <li key={label} className="flex items-center gap-2">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gold shrink-0" aria-hidden="true">
                <path d={path} />
              </svg>
              <span>{label}</span>
            </li>
          ))}
        </ul>

        {/* ── Product details / shipping / returns ── */}
        <dl className="mt-6 divide-y divide-white/10 border-t border-white/10 text-sm">
          {PRODUCT_FACTS.map(({ term, desc }) => (
            <div key={term} className="py-3 grid grid-cols-[7rem_1fr] gap-3">
              <dt className="text-white/50 font-semibold">{term}</dt>
              <dd className="text-white/75 leading-relaxed">{desc}</dd>
            </div>
          ))}
        </dl>

        {/* ── Reviews (honest — no fabricated ratings) ── */}
        <div className="mt-6 pt-5 border-t border-white/10">
          <div className="flex items-center gap-3">
            <span className="flex gap-0.5" aria-label="No reviews yet">
              {[1, 2, 3, 4, 5].map((n) => (
                <svg
                  key={n}
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-gold"
                  aria-hidden="true"
                >
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              ))}
            </span>
            <p className="text-sm text-white/50">
              No reviews yet — be the first after your purchase.
            </p>
          </div>
        </div>
      </div>

      <div className="md:hidden fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-navy-deep/95 px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] shadow-2xl shadow-black/60 backdrop-blur">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-white">{product.name}</p>
            <p className="text-base font-bold text-gold-text">{formatPrice(product.price_cents)}</p>
          </div>
          <button
            type="button"
            onClick={handleAdd}
            disabled={!activeVariant && rawVariants.length > 0}
            className="shrink-0 inline-flex items-center justify-center rounded-lg bg-gold px-5 py-3 text-sm font-bold text-white shadow-lg shadow-gold/25 hover:bg-gold-bright disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-black"
          >
            Add to cart
          </button>
        </div>
      </div>
    </div>
  )
}
